import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { User } from '../user.model';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { Department, DepartmentSearchEvent } from '../../department/department.model';
import { PageEvent } from '@angular/material/paginator';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { PagedResult } from 'src/app/shared/models/paging';
import { DragDropType } from 'src/app/shared/enums/drap-drop-type';
import { SnackbarService } from 'ok-angular-shared/components';
import { UserService } from 'src/app/shared/services/user.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DragDropRelationComponent } from '../../../shared/components/drag-drop-relation/drag-drop-relation.component';
import { forkJoin, Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface IDepartmentRestrictionForm {
  organizationId: FormControl<number>;
  userId: FormControl<number>;
  departmentIds: FormControl<number[]>;
  departmentNames: FormControl<string[]>;
}

@Component({
  selector: 'adm-department-restriction-modal',
  templateUrl: './department-restriction-modal.component.html',
  styleUrls: ['./department-restriction-modal.component.scss'],
  imports: [DragDropRelationComponent, MatDialogModule, MatButtonModule, MatIconModule, ReactiveFormsModule],
})
export class DepartmentRestrictionModalComponent {
  dialogRef = inject<MatDialogRef<DepartmentRestrictionModalComponent>>(MatDialogRef);
  data = inject<{
    user: User;
  }>(MAT_DIALOG_DATA);
  private departmentService = inject(DepartmentService);
  private userService = inject(UserService);
  private snackbarService = inject(SnackbarService);
  private cdr = inject(ChangeDetectorRef);

  get userId() {
    return this.departmentRestrictionFormGroup.controls.userId;
  }
  get departmentIds() {
    return this.departmentRestrictionFormGroup.controls.departmentIds;
  }
  get departmentNames() {
    return this.departmentRestrictionFormGroup.controls.departmentNames;
  }

  public user: User;
  public filteredDepartments: PagedResult<Department>;
  public filteredSelectedDepartments: Department[] = [];
  public saved = false;
  public saving = false;
  public dragDropTypeEnum: typeof DragDropType = DragDropType;

  private departmentUpdate$ = new Subject<Department>();
  private searchQuery$ = new BehaviorSubject<string>('');
  private departments$ = new BehaviorSubject<PagedResult<Department> | null>(null);
  private selectedDepartments$ = new BehaviorSubject<Department[]>([]);
  private destroyRef = inject(DestroyRef);

  get departments(): PagedResult<Department> {
    return this.departments$.getValue()!;
  }

  get selectedDepartments(): Department[] {
    return this.selectedDepartments$.getValue();
  }

  public searchEvent: DepartmentSearchEvent = new DepartmentSearchEvent({
    paging: { pageIndex: 0, pageSize: 10000 } as PageEvent,
  });

  public departmentRestrictionFormGroup: FormGroup<IDepartmentRestrictionForm>;

  constructor() {
    this.user = this.data.user;

    this.departmentRestrictionFormGroup = this.initFormGroup();

    this.searchEvent.organizationId = this.user.organizationId;

    this.initDepartmentUpdateSubscription();
    this.initSearchSubscription();
    this.loadDepartments();
  }

  private loadDepartments(): void {
    forkJoin({
      allDepartments: this.departmentService.getDepartments(this.searchEvent),
      userDepartments: this.userService.getUserDepartments(this.user.id),
    }).subscribe({
      next: ({ allDepartments, userDepartments }) => {
        const userDepartmentIds = new Set(userDepartments.map(d => d.id));

        // Departments assigned to the user (right side)
        const selected = allDepartments.items.filter(d => userDepartmentIds.has(d.id)).sort(this.compareDepartmentsByName);

        // Departments not assigned to the user (left side)
        const notAssigned = allDepartments.items.filter(d => !userDepartmentIds.has(d.id)).sort(this.compareDepartmentsByName);

        const departments: PagedResult<Department> = {
          items: notAssigned,
          pageIndex: 0,
          pageSize: 10000,
          totalCount: notAssigned.length,
        };

        this.departmentIds.setValue(selected.map(d => d.id));
        this.departmentNames.setValue(selected.map(d => d.name));

        this.departments$.next(departments);
        this.selectedDepartments$.next(selected);
        this.cdr.detectChanges();
      },
      error: () => {
        this.snackbarService.error('Kunne ikke hente afdelinger.');
        this.cdr.detectChanges();
      },
    });
  }

  public initFormGroup(): FormGroup<IDepartmentRestrictionForm> {
    return new FormGroup<IDepartmentRestrictionForm>({
      organizationId: new FormControl<number>(this.user.organizationId, { nonNullable: true, validators: [Validators.required] }),
      userId: new FormControl<number>(this.user.id, { nonNullable: true, validators: [Validators.required] }),
      departmentIds: new FormControl<number[]>([], { nonNullable: true }),
      departmentNames: new FormControl<string[]>([], { nonNullable: true }),
    });
  }

  public cancel() {
    this.dialogRef.close();
  }

  public close() {
    this.dialogRef.close(this.user);
  }

  onDrop(event: CdkDragDrop<Department[]>) {
    const item = event.previousContainer.data[event.previousIndex] as Department;
    this.onUpdate(item);
  }

  onUpdate(item: Department) {
    const isSelected = this.selectedDepartments.some(d => d.id === item.id);

    if (isSelected) {
      this.onRightToLeft(item);
    } else {
      this.onLeftToRight(item);
    }

    this.departmentUpdate$.next(item);
  }

  private onLeftToRight(item: Department): void {
    const selected = [...this.selectedDepartments, item];
    const deptItems = this.departments.items.filter(d => d.id !== item.id);

    this.selectedDepartments$.next(selected);
    this.departments$.next({
      ...this.departments,
      items: deptItems,
    });
  }

  private onRightToLeft(item: Department): void {
    const selected = this.selectedDepartments.filter(d => d.id !== item.id);
    const deptItems = [...this.departments.items, item];

    this.selectedDepartments$.next(selected);
    this.departments$.next({
      ...this.departments,
      items: deptItems,
    });
  }

  private initDepartmentUpdateSubscription(): void {
    this.departmentUpdate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.departmentRestrictionFormGroup.markAsDirty();
      this.sortAndUpdateDepartments();
      this.syncFormControls();
    });
  }

  private sortAndUpdateDepartments(): void {
    const sortedSelected = [...this.selectedDepartments].sort(this.compareDepartmentsByName);
    const sortedDeptItems = [...this.departments.items].sort(this.compareDepartmentsByName);

    this.selectedDepartments$.next(sortedSelected);
    this.departments$.next({
      ...this.departments,
      items: sortedDeptItems,
      totalCount: sortedDeptItems.length,
    });
  }

  private syncFormControls(): void {
    this.departmentIds.setValue(this.selectedDepartments.map(d => d.id));
    this.departmentNames.setValue(this.selectedDepartments.map(d => d.name));
  }

  private compareDepartmentsByName(a: Department, b: Department): number {
    const na = a.name.toLowerCase();
    const nb = b.name.toLowerCase();

    if (na < nb) {
      return -1;
    }
    if (na > nb) {
      return 1;
    }
    return 0;
  }

  public onSearch(searchString: string) {
    this.searchQuery$.next(searchString);
  }

  private initSearchSubscription(): void {
    const debouncedQuery$ = this.searchQuery$.pipe(
      debounceTime(150),
      startWith(this.searchQuery$.getValue()),
      distinctUntilChanged(),
      map(query => query.toLowerCase().trim())
    );

    combineLatest([debouncedQuery$, this.departments$, this.selectedDepartments$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([query, departments, selectedDepartments]) => {
        if (!departments) return;

        this.filteredDepartments = this.filterDepartmentsList(departments, query);
        this.filteredSelectedDepartments = this.filterDepartments(selectedDepartments, query);
        this.cdr.detectChanges();
      });
  }

  private filterDepartmentsList(departments: PagedResult<Department>, query: string): PagedResult<Department> {
    if (!query) {
      return { ...departments };
    }

    const filteredItems = this.filterDepartments(departments.items, query);
    return {
      items: filteredItems,
      pageIndex: 0,
      pageSize: 10000,
      totalCount: departments.totalCount,
    } as PagedResult<Department>;
  }

  private filterDepartments(departments: Department[], query: string): Department[] {
    if (!query) {
      return [...departments];
    }
    return departments.filter(d => d.name.toLowerCase().includes(query));
  }

  public async saveAndClose() {
    this.saving = !this.departmentRestrictionFormGroup.invalid;
    if (!this.saving) {
      return;
    }
    this.userService.restrictPortalUserDeparments(this.departmentRestrictionFormGroup.getRawValue()).subscribe({
      next: () => {
        this.saving = false;
        this.saved = true;

        this.snackbarService.success('Ændringer til afdelingsbegrænsning er gemt.');
        this.departmentRestrictionFormGroup.markAsPristine();
      },
    });
  }
}
